import { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Search, Edit, Trash2, Users, ShieldCheck, ShieldAlert, Shield, Package,
  Loader2, Plus, Key, Eye, Settings, FileText, Heart, Pill, DollarSign, Truck,
  Building2, ShoppingCart, Banknote, Receipt, LayoutDashboard, Tag, LogOut, KeyRound,
} from 'lucide-react';
import { skeletonKeys } from '../lib/listKeys';
import { UserAccessActions, UserAccessBadges } from '../components/users/UserAccessActions';

const ROLE_CONFIG = {
  super_admin: { label: 'Super Admin', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30', icon: ShieldAlert },
  admin: { label: 'Admin (Legacy)', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30', icon: ShieldAlert },
  pharmacist: { label: 'Pharmacist', color: 'bg-primary/15 text-primary border-primary/30', icon: ShieldCheck },
  inventory_manager: { label: 'Inventory Mgr', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30', icon: Package },
  finance: { label: 'Finance', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30', icon: DollarSign },
  cashier: { label: 'Cashier', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30', icon: Shield },
};

const ASSIGNABLE_ROLES = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'pharmacist', label: 'Pharmacist' },
  { value: 'inventory_manager', label: 'Inventory Manager' },
  { value: 'finance', label: 'Finance Officer' },
  { value: 'cashier', label: 'Cashier / Tech' },
];

// Permission categories — fully driven by `/api/rbac/permissions`. The
// backend returns the catalog grouped by category along with display
// metadata (label / icon / order). Adding a new permission to
// `backend/rbac.py` automatically surfaces here without any frontend
// change.

// Map icon-name string → lucide component. Unknown names fall back to Tag.
const ICON_MAP = {
  LayoutDashboard, ShoppingCart, Receipt, Pill, Package, Truck,
  DollarSign, Users, Banknote, Heart, Settings, Building2, Tag, Eye, FileText,
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', role: 'pharmacist', email: '', password: '', phone: '', custom_permissions: { granted: [], revoked: [] }, tenant_id: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [signOutConfirm, setSignOutConfirm] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [busy, setBusy] = useState(false);
  const [allPermissions, setAllPermissions] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState({ categories: [], grouped: {} });
  const [branches, setBranches] = useState([]);

  const isSuperAdmin = currentUser?.role === 'super_admin' || currentUser?.role === 'admin';

  const fetchBranches = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await api.get('/tenants');
      setBranches((res.data.tenants || []).filter(t => t.is_active !== false));
    } catch { /* ignore */ }
  }, [isSuperAdmin]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users', { params: { search, limit: 100 } });
      setUsers(res.data.users || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) toast.error('Admin access required');
      else console.error(err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  const fetchPermissions = useCallback(async () => {
    try {
      const res = await api.get('/rbac/permissions');
      setAllPermissions(res.data.permissions || []);
      setPermissionCatalog({
        categories: res.data.categories || [],
        grouped: res.data.grouped || {},
      });
    } catch (err) {
      console.error('Failed to fetch permissions', err);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchUsers, 300);
    return () => clearTimeout(t);
  }, [fetchUsers]);

  useEffect(() => {
    fetchPermissions();
    fetchBranches();
  }, [fetchPermissions, fetchBranches]);

  const openEdit = (u) => {
    setEditUser(u);
    // Normalise legacy list format to {granted, revoked}
    const raw = u.custom_permissions || {};
    const cp = Array.isArray(raw)
      ? { granted: raw, revoked: [] }
      : { granted: raw.granted || [], revoked: raw.revoked || [] };
    setFormData({ 
      name: u.name, 
      role: u.role || 'pharmacist', 
      email: u.email, 
      password: '',
      phone: u.phone || '',
      custom_permissions: cp,
      tenant_id: u.tenant_id || ''
    });
    setEditDialog(true);
  };

  const openCreate = () => {
    setFormData({ name: '', role: 'pharmacist', email: '', password: '', phone: '', custom_permissions: { granted: [], revoked: [] }, tenant_id: '' });
    setCreateDialog(false);
    setCreateDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { 
        name: formData.name, 
        role: formData.role,
        phone: formData.phone?.trim() || '',
        custom_permissions: formData.custom_permissions.length > 0 ? formData.custom_permissions : null
      };
      if (isSuperAdmin && formData.tenant_id) {
        payload.tenant_id = formData.tenant_id;
      }
      await api.put(`/users/${editUser.user_id}`, payload);
      toast.success('User updated');
      setEditDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      const params = {};
      if (isSuperAdmin && formData.tenant_id) {
        params.tenant_id = formData.tenant_id;
      }
      await api.post('/users', { name: formData.name, email: formData.email, password: formData.password, role: formData.role, phone: formData.phone?.trim() || '' }, { params });
      toast.success('User created');
      setCreateDialog(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success('User deleted');
      setDeleteConfirm(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  const [pinDialog, setPinDialog] = useState(null);
  const [newPin, setNewPin] = useState('');

  const savePin = async () => {
    setBusy(true);
    try {
      await api.post(`/users/${pinDialog.user_id}/shift-pin`, { pin: newPin });
      toast.success(`Till PIN set for ${pinDialog.name}`);
      setPinDialog(null);
      setNewPin('');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not set PIN');
    }
    setBusy(false);
  };

  const clearPin = async () => {
    setBusy(true);
    try {
      await api.delete(`/users/${pinDialog.user_id}/shift-pin`);
      toast.success(`Till PIN cleared for ${pinDialog.name} — they can set a new one`);
      setPinDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not clear PIN');
    }
    setBusy(false);
  };

  const handleSignOutAll = async (userId) => {
    setSigningOut(true);
    try {
      const { data } = await api.post(`/users/${userId}/sign-out-all`);
      toast.success(`Signed out all devices for ${data.email}`);
      setSignOutConfirm(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not sign out devices');
    }
    setSigningOut(false);
  };

  const toggleCustomPermission = (permKey) => {
    setFormData(prev => {
      const rolePerms = editUser?.role_info?.permissions || [];
      const hasFromRole = rolePerms.includes(permKey);
      const cp = prev.custom_permissions || { granted: [], revoked: [] };
      // Normalise legacy list
      const granted = new Set(Array.isArray(cp) ? cp : (cp.granted || []));
      const revoked = new Set(Array.isArray(cp) ? [] : (cp.revoked || []));

      if (hasFromRole) {
        // Toggle revocation of a role-default permission
        if (revoked.has(permKey)) { revoked.delete(permKey); } else { revoked.add(permKey); granted.delete(permKey); }
      } else {
        // Toggle granting of a non-role permission
        if (granted.has(permKey)) { granted.delete(permKey); } else { granted.add(permKey); revoked.delete(permKey); }
      }
      return { ...prev, custom_permissions: { granted: [...granted], revoked: [...revoked] } };
    });
  };

  const resetToDefaults = () => {
    setFormData(prev => ({ ...prev, custom_permissions: { granted: [], revoked: [] } }));
  };

  // Get role's base permissions
  const getRolePermissions = (role) => {
    const roleData = ASSIGNABLE_ROLES.find(r => r.value === role);
    const roleInfo = editUser?.role_info;
    return roleInfo?.permissions || [];
  };

  return (
    <div className="space-y-4" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            User Management
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{total} users registered</p>
        </div>
        <Button onClick={openCreate} className="gap-2" data-testid="create-user-btn">
          <Plus className="h-4 w-4" /> Add User
        </Button>
      </div>

      <Card>
        <CardContent className="p-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="users-search" />
          </div>
        </CardContent>
      </Card>

      <Card data-testid="users-table-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead className="hidden md:table-cell">Branch</TableHead>
              <TableHead className="hidden md:table-cell">Custom Perms</TableHead>
              <TableHead className="hidden md:table-cell">Joined</TableHead>
              <TableHead className="text-right w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              skeletonKeys(3, 'user-skeleton').map((key) => (
                <TableRow key={key}><TableCell colSpan={7}><div className="h-10 bg-muted rounded animate-pulse" /></TableCell></TableRow>
              ))
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p>No users found</p>
                </TableCell>
              </TableRow>
            ) : users.map(u => {
              const cfg = ROLE_CONFIG[u.role] || ROLE_CONFIG.cashier;
              const isSelf = u.user_id === currentUser?.user_id;
              const hasCustomPerms = u.custom_permissions && u.custom_permissions.length > 0;
              return (
                <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                        {u.name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <span className="font-medium text-sm">{u.name} {isSelf && <span className="text-xs text-muted-foreground">(you)</span>}<UserAccessBadges u={u} /></span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-xs ${cfg.color}`}>
                      <cfg.icon className="h-3 w-3 mr-1" /> {cfg.label}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {u.tenant_name ? (
                      <Badge variant="outline" className="text-xs gap-1">
                        <Building2 className="h-3 w-3" /> {u.tenant_name}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    {hasCustomPerms ? (
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Key className="h-3 w-3" /> +{u.custom_permissions.length}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)} data-testid={`edit-user-${u.user_id}`}>
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title={u.pin_set ? 'Till PIN set — change or clear' : 'No till PIN — set one'}
                        onClick={() => { setPinDialog(u); setNewPin(''); }}
                        data-testid={`pin-user-${u.user_id}`}
                      >
                        <KeyRound className={`h-3.5 w-3.5 ${u.pin_set ? 'text-emerald-600' : 'text-muted-foreground'}`} />
                      </Button>
                      {!isSelf && (
                        <UserAccessActions u={u} onDone={fetchUsers}
                          canUnlock={['super_admin', 'admin'].includes(currentUser?.role)} />
                      )}
                      <Button
                        variant="ghost" size="icon" className="h-8 w-8"
                        title="Sign out all devices"
                        onClick={() => setSignOutConfirm(u)}
                        data-testid={`signout-user-${u.user_id}`}
                      >
                        <LogOut className="h-3.5 w-3.5" />
                      </Button>
                      {!isSelf && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setDeleteConfirm(u.user_id)} data-testid={`delete-user-${u.user_id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* Edit User Dialog with Permissions */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5" /> Edit User
            </DialogTitle>
            <DialogDescription>
              Update user details and customize their permissions
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="permissions" className="gap-1.5">
                <Key className="h-3.5 w-3.5" /> Permissions
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={formData.email} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="e.g. 0244111222" data-testid="edit-user-phone" />
                <p className="text-xs text-muted-foreground">Ghana mobile number, used for SMS follow-up reminders (e.g. stale checkout reviews).</p>
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASSIGNABLE_ROLES.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Role determines base permissions</p>
              </div>
              {isSuperAdmin && branches.length > 0 && (
                <div className="space-y-2">
                  <Label>Branch</Label>
                  <Select value={formData.tenant_id || 'none'} onValueChange={(v) => setFormData(p => ({ ...p, tenant_id: v === 'none' ? '' : v }))}>
                    <SelectTrigger data-testid="edit-user-branch"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {branches.map(b => (
                        <SelectItem key={b.tenant_id} value={b.tenant_id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">Assign user to a specific branch</p>
                </div>
              )}
            </TabsContent>
            
            <TabsContent value="permissions" className="mt-4">
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Customise permissions for this user. Role defaults are shown as muted; overrides are highlighted.
                  </p>
                  <Button variant="ghost" size="sm" onClick={resetToDefaults} className="text-xs" data-testid="reset-permissions-btn">
                    Reset to defaults
                  </Button>
                </div>
                {(() => {
                  const cp = formData.custom_permissions || {};
                  const g = Array.isArray(cp) ? cp : (cp.granted || []);
                  const r = Array.isArray(cp) ? [] : (cp.revoked || []);
                  return (g.length > 0 || r.length > 0) && (
                    <div className="flex flex-wrap gap-1">
                      {g.map(p => {
                        const meta = allPermissions.find(x => x.key === p);
                        return <Badge key={p} className="text-xs bg-emerald-100 text-emerald-700 border-emerald-300" title={meta?.description}>+ {meta?.label || p}</Badge>;
                      })}
                      {r.map(p => {
                        const meta = allPermissions.find(x => x.key === p);
                        return <Badge key={p} className="text-xs bg-red-100 text-red-700 border-red-300" title={meta?.description}>- {meta?.label || p}</Badge>;
                      })}
                    </div>
                  );
                })()}
              </div>
              
              <ScrollArea className="h-[350px] pr-4">
                <div className="space-y-4">
                  {permissionCatalog.categories.map((cat) => {
                    const perms = permissionCatalog.grouped?.[cat.key] || [];
                    if (perms.length === 0) return null;
                    const CatIcon = ICON_MAP[cat.icon] || Tag;
                    const rolePerms = editUser?.role_info?.permissions || [];
                    const cp = formData.custom_permissions || {};
                    const grantedSet = new Set(Array.isArray(cp) ? cp : (cp.granted || []));
                    const revokedSet = new Set(Array.isArray(cp) ? [] : (cp.revoked || []));

                    return (
                      <Card key={cat.key} className="p-3" data-testid={`perm-cat-${cat.key}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <CatIcon className="h-4 w-4 text-primary" />
                          <span className="font-medium text-sm">{cat.label}</span>
                          <Badge variant="outline" className="text-[10px]">{perms.length}</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          {perms.map((p) => {
                            const hasFromRole = rolePerms.includes(p.key);
                            const isGranted = grantedSet.has(p.key);
                            const isRevoked = revokedSet.has(p.key);
                            const isEnabled = (hasFromRole && !isRevoked) || isGranted;
                            const isOverride = isGranted || isRevoked;

                            return (
                              <div
                                key={p.key}
                                className={`flex items-center justify-between p-2 rounded-md hover:bg-muted/50 ${isOverride ? 'ring-1 ring-primary/20 bg-primary/5' : ''}`}
                                title={p.description}
                              >
                                <span className={`text-xs ${hasFromRole && !isOverride ? 'text-muted-foreground' : ''}`}>
                                  {p.label}
                                  {hasFromRole && !isOverride && <span className="ml-1 text-[10px]">(role)</span>}
                                  {isRevoked && <span className="ml-1 text-[10px] text-red-500">(revoked)</span>}
                                  {isGranted && <span className="ml-1 text-[10px] text-emerald-600">(granted)</span>}
                                </span>
                                <Switch
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleCustomPermission(p.key)}
                                  data-testid={`perm-${p.key}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
          
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-user-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Save Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="max-w-sm" data-testid="create-user-dialog">
          <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Full Name" data-testid="create-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))} placeholder="user@pharmacy.com" data-testid="create-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input type="password" value={formData.password} onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))} placeholder="Secure password" data-testid="create-user-password" />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="e.g. 0244111222" data-testid="create-user-phone" />
              <p className="text-xs text-muted-foreground">Ghana mobile number, used for SMS follow-up reminders.</p>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(v) => setFormData(p => ({ ...p, role: v }))}>
                <SelectTrigger data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNABLE_ROLES.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isSuperAdmin && branches.length > 0 && (
              <div className="space-y-2">
                <Label>Branch</Label>
                <Select value={formData.tenant_id || 'none'} onValueChange={(v) => setFormData(p => ({ ...p, tenant_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger data-testid="create-user-branch"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Unassigned</SelectItem>
                    {branches.map(b => (
                      <SelectItem key={b.tenant_id} value={b.tenant_id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} data-testid="create-user-submit">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Create User
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader><DialogTitle>Delete User</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This will permanently delete this user and all their sessions.</p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm)} data-testid="confirm-delete-user">Delete</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!signOutConfirm} onOpenChange={() => setSignOutConfirm(null)}>
        <DialogContent className="max-w-sm" data-testid="signout-user-dialog">
          <DialogHeader><DialogTitle>Sign out all devices</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {signOutConfirm?.name} will be signed out everywhere — browser sessions and
            desktop terminals, including any terminal still inside its 14-day offline
            window. They keep their password and can sign back in.
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setSignOutConfirm(null)}>Cancel</Button>
            <Button
              onClick={() => handleSignOutAll(signOutConfirm.user_id)}
              disabled={signingOut}
              data-testid="confirm-signout-user"
            >
              {signingOut && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Sign out everywhere
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <Dialog open={!!pinDialog} onOpenChange={() => setPinDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="pin-user-dialog">
          <DialogHeader><DialogTitle>Till PIN — {pinDialog?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {pinDialog?.pin_set
              ? 'This user has a till PIN. Set a new one for them, or clear it so they can choose their own at the counter.'
              : 'Give this user a 4-digit PIN so they can lock the till at handover instead of signing out.'}
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">New 4-digit PIN</Label>
            <Input
              type="password" inputMode="numeric" maxLength={4} value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ''))}
              placeholder="••••" data-testid="user-pin-input"
            />
          </div>
          <div className="flex justify-end gap-2">
            {pinDialog?.pin_set && (
              <Button variant="outline" onClick={clearPin} disabled={busy} data-testid="user-pin-clear">
                Clear PIN
              </Button>
            )}
            <Button onClick={savePin} disabled={busy || newPin.length !== 4} data-testid="user-pin-save">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Set PIN
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
